<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import EmailSendingDomainsAPI from 'dashboard/api/emailSendingDomains';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const POLL_INTERVAL = 20000;
const PROVIDERS = ['GOOGLE_WORKSPACE', 'GMAIL', 'MICROSOFT', 'OTHER'];

const liveInbox = ref({ ...props.inbox });
const sendingDomain = ref(null);
const domainError = ref('');
const isLoadingSendingDomain = ref(false);
const isCheckingDns = ref(false);
const isConfiguringCloudflare = ref(false);
const selectedProvider = ref('GOOGLE_WORKSPACE');
const isSendingTest = ref(false);
let pollTimer = null;

const forwardingConfirmed = computed(
  () => !!liveInbox.value.forwarding_confirmed
);
const gmailForwardingCode = computed(() =>
  forwardingConfirmed.value ? null : liveInbox.value.gmail_forwarding_code
);
const gmailForwardingLink = computed(() =>
  forwardingConfirmed.value ? null : liveInbox.value.gmail_forwarding_link
);
const gmailAutoConfirmed = computed(
  () =>
    !forwardingConfirmed.value &&
    !!liveInbox.value.gmail_forwarding_auto_confirmed
);
const showGoogleCallout = computed(
  () => !!(gmailForwardingCode.value || gmailForwardingLink.value)
);
const forwardingTestSent = computed(
  () => !forwardingConfirmed.value && !!liveInbox.value.forwarding_test_sent_at
);
const forwardingStatusKey = computed(() => {
  if (forwardingConfirmed.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.FORWARDING.CONFIRMED';
  }

  if (forwardingTestSent.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.FORWARDING.TESTING';
  }

  if (showGoogleCallout.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.WAITING_AFTER_CONFIRMATION';
  }

  return 'INBOX_MGMT.EMAIL_SETUP.FORWARDING.WAITING';
});
const forwardingBadgeKey = computed(() => {
  if (forwardingConfirmed.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.STATUS.ACTIVE';
  }

  if (showGoogleCallout.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.STATUS.NEEDS_ACTION';
  }

  return 'INBOX_MGMT.EMAIL_SETUP.STATUS.PENDING';
});
const domainVerified = computed(() =>
  sendingDomain.value
    ? sendingDomain.value.verified
    : !!liveInbox.value.verified_for_sending
);
const dnsRecords = computed(() => sendingDomain.value?.dns_records || []);
const verifiedRecordCount = computed(
  () =>
    dnsRecords.value.filter(
      record => record.dns_status === 'found' || record.status === 'verified'
    ).length
);
const allDnsRecordsFound = computed(
  () =>
    dnsRecords.value.length > 0 &&
    verifiedRecordCount.value === dnsRecords.value.length
);
const domainStatusKey = computed(() => {
  if (domainVerified.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.STATUS.VERIFIED';
  }

  if (allDnsRecordsFound.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.STATUS.VERIFYING';
  }

  if (domainError.value) {
    return 'INBOX_MGMT.EMAIL_SETUP.STATUS.NEEDS_ACTION';
  }

  return 'INBOX_MGMT.EMAIL_SETUP.STATUS.PENDING';
});
const domainStatusClasses = computed(() =>
  domainVerified.value
    ? 'bg-n-teal-3 text-n-teal-11'
    : 'bg-n-amber-3 text-n-amber-11'
);
const lastCheckedAt = computed(() => {
  if (!sendingDomain.value?.last_synced_at) return '';

  return new Date(sendingDomain.value.last_synced_at).toLocaleString();
});
const setupComplete = computed(
  () => forwardingConfirmed.value && domainVerified.value
);

const announcedGoogleConfirmation = ref(showGoogleCallout.value);
const announcedForwardingConfirmed = ref(forwardingConfirmed.value);
const announcedDomainVerified = ref(domainVerified.value);

const refreshInbox = async () => {
  const { data } = await InboxesAPI.show(props.inbox.id);
  liveInbox.value = data;
};

const refreshSendingDomain = async () => {
  if (!sendingDomain.value || sendingDomain.value.verified) return;
  const { data } = await EmailSendingDomainsAPI.show(sendingDomain.value.id);
  sendingDomain.value = data;
};

const poll = async () => {
  clearTimeout(pollTimer);
  try {
    await refreshInbox();
    await refreshSendingDomain();
  } catch (error) {
    // transient poll errors are retried on the next tick
  }
  if (!setupComplete.value) {
    pollTimer = setTimeout(poll, POLL_INTERVAL);
  }
};

const startPolling = () => {
  if (setupComplete.value) return;

  clearTimeout(pollTimer);
  pollTimer = setTimeout(poll, POLL_INTERVAL);
};

const setupSendingDomain = async () => {
  if (isLoadingSendingDomain.value) return;

  isLoadingSendingDomain.value = true;
  domainError.value = '';
  try {
    const { data } = await EmailSendingDomainsAPI.create({
      email_sending_domain: { domain: props.inbox.sending_domain },
    });
    sendingDomain.value = data;
  } catch (error) {
    domainError.value =
      error.response?.data?.error || t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.ERROR');
  } finally {
    isLoadingSendingDomain.value = false;
  }
};

watch(showGoogleCallout, isVisible => {
  if (!isVisible || announcedGoogleConfirmation.value) return;

  announcedGoogleConfirmation.value = true;
  useAlert(t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.DETECTED_ALERT'));
});

watch(forwardingConfirmed, isConfirmed => {
  if (!isConfirmed || announcedForwardingConfirmed.value) return;

  announcedForwardingConfirmed.value = true;
  useAlert(t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.CONFIRMED_ALERT'));
});

watch(domainVerified, isVerified => {
  if (!isVerified || announcedDomainVerified.value) return;

  announcedDomainVerified.value = true;
  useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.VERIFIED_ALERT'));
});

onMounted(async () => {
  if (!domainVerified.value) {
    await setupSendingDomain();
  }
  if (!setupComplete.value) {
    startPolling();
  }
});

onBeforeUnmount(() => clearTimeout(pollTimer));

const onCopy = async value => {
  await copyTextToClipboard(value);
  useAlert(t('INBOX_MGMT.EMAIL_SETUP.COPY_SUCCESSFUL'));
};

const recordVerified = record =>
  record.dns_status === 'found' || record.status === 'verified';
const recordPriority = record => {
  if (record.type !== 'MX') {
    return t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.NOT_APPLICABLE');
  }

  return record.priority ?? t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.PRIORITY_MISSING');
};
const recordPriorityMissing = record =>
  record.type === 'MX' &&
  (record.priority === undefined || record.priority === null);

const recheckDns = async () => {
  if (!sendingDomain.value || isCheckingDns.value) return;

  isCheckingDns.value = true;
  domainError.value = '';
  try {
    const { data } = await EmailSendingDomainsAPI.recheck(
      sendingDomain.value.id
    );
    sendingDomain.value = data;
    if (!data.verified) {
      const messageOptions = {
        found: verifiedRecordCount.value,
        total: dnsRecords.value.length,
      };
      const message =
        verifiedRecordCount.value === dnsRecords.value.length
          ? t(
              'INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECHECK_PROVIDER_PENDING',
              messageOptions
            )
          : t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECHECK_PENDING', messageOptions);
      useAlert(message);
      startPolling();
    }
  } catch (error) {
    domainError.value =
      error.response?.data?.error || t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.ERROR');
    useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECHECK_ERROR'));
  } finally {
    isCheckingDns.value = false;
  }
};

const retryDomainSetup = () =>
  sendingDomain.value ? recheckDns() : setupSendingDomain();

const configureWithCloudflare = async () => {
  if (isConfiguringCloudflare.value) return;

  isConfiguringCloudflare.value = true;
  domainError.value = '';
  try {
    const { data } = await InboxesAPI.provisionCloudflare(props.inbox.id);
    const provisioning = data.cloudflare_provisioning;
    liveInbox.value.cloudflare_provisioning = provisioning;

    if (provisioning.status === 'active') {
      useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.CLOUDFLARE_ACTIVE'));
      await recheckDns();
    } else if (provisioning.status === 'pending_destination_verification') {
      useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.CLOUDFLARE_PENDING'));
      await recheckDns();
    } else {
      domainError.value =
        provisioning.error ||
        t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.CLOUDFLARE_ERROR');
      useAlert(domainError.value);
    }
  } catch (error) {
    domainError.value =
      error.response?.data?.error ||
      t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.CLOUDFLARE_ERROR');
    useAlert(domainError.value);
  } finally {
    isConfiguringCloudflare.value = false;
  }
};

const shareEmail = ref('');
const isSharing = ref(false);
const sendForwardingTest = async () => {
  if (isSendingTest.value || forwardingConfirmed.value) return;
  isSendingTest.value = true;
  try {
    const { data } = await InboxesAPI.testForwarding(props.inbox.id);
    liveInbox.value = data;
    useAlert(t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_SENT'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_ERROR'));
  } finally {
    isSendingTest.value = false;
  }
};

const shareRecords = async () => {
  if (!shareEmail.value || !sendingDomain.value) return;
  isSharing.value = true;
  try {
    await EmailSendingDomainsAPI.share(
      sendingDomain.value.id,
      shareEmail.value
    );
    useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SHARE.SUCCESS'));
    shareEmail.value = '';
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SHARE.ERROR'));
  } finally {
    isSharing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 text-left">
    <div
      class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1"
    >
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12 mb-1">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-0">
            {{
              $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.SUBTITLE', {
                email: inbox.email,
              })
            }}
          </p>
        </div>
        <span
          class="px-2 py-0.5 rounded-lg text-xs font-medium whitespace-nowrap"
          :class="
            forwardingConfirmed
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-amber-3 text-n-amber-11'
          "
        >
          {{ $t(forwardingBadgeKey) }}
        </span>
      </div>

      <woot-code :script="inbox.forward_to_email" />

      <div class="flex flex-wrap gap-2">
        <button
          v-for="provider in PROVIDERS"
          :key="provider"
          class="px-3 py-1 rounded-full text-sm outline outline-1 -outline-offset-1 transition-colors"
          :class="
            selectedProvider === provider
              ? 'outline-n-brand text-n-blue-text bg-n-blue-2'
              : 'outline-n-weak text-n-slate-11 hover:bg-n-slate-2'
          "
          @click="selectedProvider = provider"
        >
          {{
            $t(`INBOX_MGMT.EMAIL_SETUP.FORWARDING.PROVIDERS.${provider}.NAME`)
          }}
        </button>
      </div>
      <p class="text-sm text-n-slate-11 mb-0">
        {{
          $t(
            `INBOX_MGMT.EMAIL_SETUP.FORWARDING.PROVIDERS.${selectedProvider}.STEPS`
          )
        }}
      </p>

      <div
        v-if="forwardingConfirmed"
        class="flex items-start gap-3 py-3 px-4 rounded-xl bg-n-teal-3 outline outline-1 -outline-offset-1 outline-n-teal-4"
      >
        <span class="i-lucide-circle-check size-5 text-n-teal-10 mt-0.5" />
        <div>
          <p class="text-sm font-medium text-n-teal-11 mb-1">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.SUCCESS.TITLE') }}
          </p>
          <p class="text-sm text-n-teal-11 mb-0">
            {{
              $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.SUCCESS.BODY', {
                email: inbox.email,
              })
            }}
          </p>
        </div>
      </div>

      <div
        v-if="showGoogleCallout"
        class="flex flex-col gap-3 py-3 px-4 rounded-xl bg-n-teal-3 outline outline-1 -outline-offset-1 outline-n-teal-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <p class="text-sm font-medium text-n-teal-11 mb-1">
              {{
                gmailAutoConfirmed
                  ? $t(
                      'INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.AUTO_CONFIRMED'
                    )
                  : $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.ACTION_NEEDED')
              }}
            </p>
            <p
              v-if="gmailForwardingCode"
              class="text-lg font-semibold tracking-widest text-n-teal-11 mb-1"
            >
              {{ gmailForwardingCode }}
            </p>
            <p class="text-sm text-n-teal-11 mb-0">
              {{ $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.NEXT_STEP') }}
            </p>
          </div>
          <button
            v-if="gmailForwardingCode"
            class="text-sm text-n-teal-11 underline"
            @click="onCopy(gmailForwardingCode)"
          >
            {{ $t('INBOX_MGMT.EMAIL_SETUP.COPY') }}
          </button>
          <a
            v-else-if="gmailForwardingLink && !gmailAutoConfirmed"
            :href="gmailForwardingLink"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-n-teal-11 underline whitespace-nowrap"
          >
            {{ $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.GOOGLE.CONFIRM_LINK') }}
          </a>
        </div>
      </div>

      <div
        v-if="!forwardingConfirmed"
        class="flex flex-wrap items-center justify-between gap-3 py-3 px-4 rounded-xl bg-n-slate-2 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <p class="text-sm text-n-slate-11 mb-0">
          {{
            forwardingTestSent
              ? $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_SENT_DETAIL')
              : $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_PROMPT')
          }}
        </p>
        <button
          type="button"
          :disabled="isSendingTest"
          class="px-3 py-1.5 rounded-lg text-sm outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12 hover:bg-n-slate-3 disabled:opacity-60"
          @click="sendForwardingTest"
        >
          {{
            isSendingTest
              ? $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_SENDING')
              : $t('INBOX_MGMT.EMAIL_SETUP.FORWARDING.TEST_BUTTON')
          }}
        </button>
      </div>

      <div class="flex items-center gap-2 text-sm text-n-slate-11">
        <span
          v-if="forwardingConfirmed"
          class="i-lucide-circle-check text-n-teal-10 size-4 flex-shrink-0"
        />
        <span
          v-else
          class="i-lucide-loader-circle animate-spin size-4 flex-shrink-0"
        />
        {{
          $t(forwardingStatusKey, {
            email: inbox.email,
          })
        }}
      </div>
    </div>

    <div
      class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak bg-n-solid-1"
    >
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-base font-medium text-n-slate-12 mb-1">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-0">
            {{
              domainVerified
                ? $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SUBTITLE_VERIFIED', {
                    email: inbox.email,
                  })
                : $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SUBTITLE', {
                    domain: inbox.sending_domain,
                  })
            }}
          </p>
        </div>
        <div class="flex flex-wrap items-center justify-end gap-2">
          <Button
            v-if="!domainVerified && !allDnsRecordsFound"
            :label="$t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.CLOUDFLARE_ACTION')"
            icon="i-lucide-cloud"
            size="sm"
            color="slate"
            :is-loading="isConfiguringCloudflare"
            :disabled="isConfiguringCloudflare || isCheckingDns"
            @click="configureWithCloudflare"
          />
          <Button
            v-if="sendingDomain && !domainVerified && !allDnsRecordsFound"
            :label="$t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECHECK')"
            icon="i-lucide-refresh-cw"
            size="sm"
            color="slate"
            :is-loading="isCheckingDns"
            :disabled="isCheckingDns"
            @click="recheckDns"
          />
          <span
            class="px-2 py-0.5 rounded-lg text-xs font-medium whitespace-nowrap"
            :class="domainStatusClasses"
          >
            {{ $t(domainStatusKey) }}
          </span>
        </div>
      </div>

      <div
        v-if="domainError"
        class="flex items-start gap-3 py-3 px-4 rounded-xl bg-n-amber-3 outline outline-1 -outline-offset-1 outline-n-amber-4"
      >
        <span class="i-lucide-circle-alert size-5 text-n-amber-11 mt-0.5" />
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-amber-12 mb-1">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.ERROR_TITLE') }}
          </p>
          <p class="text-sm text-n-amber-11 mb-2">
            {{ domainError }}
          </p>
          <p class="text-sm text-n-amber-11 mb-0">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.FALLBACK_NOTE') }}
          </p>
        </div>
        <button
          type="button"
          :disabled="isLoadingSendingDomain || isCheckingDns"
          class="px-3 py-1.5 rounded-lg text-sm font-medium text-n-amber-11 bg-n-amber-4 hover:bg-n-amber-5 whitespace-nowrap"
          :class="{
            'opacity-70 cursor-not-allowed':
              isLoadingSendingDomain || isCheckingDns,
          }"
          @click="retryDomainSetup"
        >
          {{
            isLoadingSendingDomain || isCheckingDns
              ? $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RETRYING')
              : $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RETRY')
          }}
        </button>
      </div>

      <div
        v-else-if="isLoadingSendingDomain"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <span
          class="i-lucide-loader-circle animate-spin size-4 flex-shrink-0"
        />
        {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.LOADING') }}
      </div>

      <div
        v-else-if="allDnsRecordsFound && !domainVerified"
        class="flex flex-wrap items-center gap-x-5 gap-y-2 py-1 text-sm"
      >
        <span class="inline-flex items-center gap-2 font-medium text-n-teal-11">
          <span class="i-lucide-circle-check size-4 flex-shrink-0" />
          {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.DNS_FOUND') }}
        </span>
        <span class="inline-flex items-center gap-2 text-n-slate-11">
          <span
            class="i-lucide-loader-circle animate-spin size-4 flex-shrink-0"
          />
          {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.PROVIDER_VERIFYING') }}
        </span>
        <span v-if="lastCheckedAt" class="text-n-slate-10">
          {{
            $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.LAST_CHECKED', {
              time: lastCheckedAt,
            })
          }}
        </span>
      </div>

      <template v-else-if="!domainVerified && !isLoadingSendingDomain">
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left text-n-slate-11">
                <th class="py-2 pe-4 font-medium">
                  {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECORD_TYPE') }}
                </th>
                <th class="py-2 pe-4 font-medium">
                  {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECORD_NAME') }}
                </th>
                <th class="py-2 pe-4 font-medium">
                  {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECORD_VALUE') }}
                </th>
                <th class="py-2 pe-4 font-medium">
                  {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECORD_PRIORITY') }}
                </th>
                <th class="py-2 font-medium">
                  {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.RECORD_STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="record in dnsRecords"
                :key="`${record.type}-${record.name}-${record.value}`"
                class="border-t border-n-weak align-top"
              >
                <td class="py-2 pe-4 font-mono text-xs text-n-slate-12">
                  {{ record.type }}
                </td>
                <td
                  class="py-2 pe-4 font-mono text-xs text-n-slate-12 break-all"
                >
                  {{ record.name }}
                </td>
                <td class="py-2 pe-4 font-mono text-xs text-n-slate-12">
                  <div class="flex items-center gap-2">
                    <span class="break-all line-clamp-2 max-w-md">
                      {{ record.value }}
                    </span>
                    <button
                      class="i-lucide-copy size-3.5 flex-shrink-0 text-n-slate-10 hover:text-n-slate-12"
                      :aria-label="$t('INBOX_MGMT.EMAIL_SETUP.COPY')"
                      @click="onCopy(record.value)"
                    />
                  </div>
                </td>
                <td
                  class="py-2 pe-4 font-mono text-xs"
                  :class="
                    recordPriorityMissing(record)
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ recordPriority(record) }}
                </td>
                <td class="py-2">
                  <span
                    class="px-2 py-0.5 rounded-lg text-xs font-medium whitespace-nowrap"
                    :class="
                      recordVerified(record)
                        ? 'bg-n-teal-3 text-n-teal-11'
                        : 'bg-n-amber-3 text-n-amber-11'
                    "
                  >
                    {{
                      recordVerified(record)
                        ? $t('INBOX_MGMT.EMAIL_SETUP.STATUS.FOUND')
                        : $t('INBOX_MGMT.EMAIL_SETUP.STATUS.CHECKING')
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-n-slate-11"
        >
          <span class="i-lucide-refresh-cw size-4 flex-shrink-0" />
          {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.AUTO_CHECK') }}
          <span v-if="lastCheckedAt" class="text-n-slate-10">
            {{
              $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.LAST_CHECKED', {
                time: lastCheckedAt,
              })
            }}
          </span>
        </div>
        <p class="text-sm text-n-slate-11 mb-0">
          {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.FALLBACK_NOTE') }}
        </p>

        <form
          class="flex flex-wrap items-center gap-2 border-t border-n-weak pt-4"
          @submit.prevent="shareRecords"
        >
          <label class="text-sm text-n-slate-11 mb-0">
            {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SHARE.LABEL') }}
          </label>
          <input
            v-model="shareEmail"
            type="email"
            required
            class="!mb-0 !w-64"
            :placeholder="$t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SHARE.PLACEHOLDER')"
          />
          <button
            type="submit"
            :disabled="isSharing"
            class="px-3 py-1.5 rounded-lg text-sm outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-12 hover:bg-n-slate-2"
          >
            {{ $t('INBOX_MGMT.EMAIL_SETUP.DOMAIN.SHARE.BUTTON') }}
          </button>
        </form>
      </template>
    </div>
  </div>
</template>
